import { useContext, useEffect, useState, useReducer, useRef } from 'react'
import { SiteBuilderContext } from 'src/siteBuilder/renderer/context/SiteBuilderContext'

import {
  Button,
  FlexGrid,
  InputLabel,
  Spacer,
  Tags,
  Typography,
  useResponsiveProp,
  Search as SearchBar,
} from '@telus-uds/components-web'

import alloyEvents from '../../../utils/alloyAnalytics/offersFilter'
import { OFFER_FILTER_I18N } from './OffersFilter.i18n'
import { SHOW_ALL_PRODUCTS, ActionType, OfferFilterReducer, initialState } from './OffersFilter.reducer'
import { ErrorCard, getCardsToShow, getDataFromQueryString, orderCards, renderOfferCard } from './OffersFilter.helpers'
import { mapDataAttrsToDataSet } from 'src/siteBuilder/utils/dataAttributes'

import Search from '@telus-uds/palette-allium/build/web/icons/Search'
import { CaretDown } from '@telus-uds/palette-allium/build/web/icons'
import WithEarlyReturn from 'src/utils/WithEarlyReturn'
import type { BasicImageProps } from '../Image/Image'
import { VIEWPORT_SIZES } from '@/constants/VIEWPORT'

export interface TagProps {
  id: string
  label: string
  urlTag?: string
}

export type offerCard = {
  id: string
  tags: TagProps[]
  anchorID?: string
  fullWidth?: boolean
  img?: BasicImageProps
  ribbon?: string
  campaignBadge?: string
  heading: React.ReactElement
  content: React.ReactElement
  cta?: React.ReactElement
  ctaSecondary?: React.ReactElement
  background?: 'alternative' | 'subtle'
}
export type OffersFilterProps = {
  sysID: string
  entryTitle: string
  heading?: string
  filterTags: TagProps[]
  resultsText: string
  offerCards: offerCard[]
  showLoadMoreBtn?: boolean
  enableSearch?: boolean
  equalCardHeight?: boolean
}

const OffersFilter = ({
  sysID,
  entryTitle,
  heading,
  filterTags,
  resultsText,
  offerCards,
  showLoadMoreBtn = true,
  enableSearch = false,
  equalCardHeight = true,
  ...rest
}: OffersFilterProps) => {
  const [isClient, setIsClient] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' && window.innerWidth < VIEWPORT_SIZES.md
  )

  useEffect(() => {
    setIsClient(true)
  }, [])

  const fullWidthCardPosition = useResponsiveProp({ md: 4, lg: 6 }, null)
  const isMobile = useResponsiveProp({ xs: true, md: false })

  const [state, dispatch] = useReducer(
    OfferFilterReducer,
    {
      allCards: offerCards,
      showLoadMoreBtn,
      urlTagValue: [SHOW_ALL_PRODUCTS],
      filterTags,
      cardTags: [],
      SSRSafeMobile: isMobileViewport,
    },
    initialState
  )

  // Track search submissions for analytics
  const pendingSearchAnalytics = useRef<{ searchTerm: string; isFirstSearch: boolean } | null>(null)

  // Use window resize listener to track viewport changes and update mobile state
  useEffect(() => {
    if (typeof window === 'undefined') return

    const RESIZE_DEBOUNCE_MS = 150 // Debounce delay for resize events
    let timeoutId: NodeJS.Timeout

    const updateMobileState = () => {
      const newIsMobile = window.innerWidth < VIEWPORT_SIZES.md
      // Use functional state update to avoid stale closure issues
      setIsMobileViewport((prevIsMobile) => {
        if (newIsMobile !== prevIsMobile) {
          dispatch({ type: ActionType.UPDATE_MOBILE_STATE, payload: newIsMobile })
          return newIsMobile
        }
        return prevIsMobile
      })
    }

    // Debounced resize handler to avoid excessive updates
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateMobileState, RESIZE_DEBOUNCE_MS)
    }

    // Listen to window resize events
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, []) // Empty dependency array - only set up once

  // Sync URL parameters to state on mount and when URL changes
  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncUrlToState = () => {
      const tagsData = getDataFromQueryString(window.location.search, 'tags', SHOW_ALL_PRODUCTS)

      dispatch({
        type: ActionType.SYNC_URL_PARAMS,
        payload: { tags: tagsData },
      })
    }

    // Sync on mount
    syncUrlToState()

    // Listen for URL changes (back/forward navigation)
    window.addEventListener('popstate', syncUrlToState)

    return () => {
      window.removeEventListener('popstate', syncUrlToState)
    }
  }, [])

  // Fire analytics only when search is submitted (searchTerm changes to non-empty value)
  useEffect(() => {
    if (pendingSearchAnalytics.current && state.searchTerm) {
      const { searchTerm, isFirstSearch } = pendingSearchAnalytics.current
      alloyEvents.search({
        searchTerm,
        numberOfResults: state.filteredCards.length,
        isFirstSearch,
      })
      pendingSearchAnalytics.current = null
    }
  }, [state.searchTerm, state.filteredCards.length])

  const { locale } = useContext(SiteBuilderContext)

  const { filterByProductLabel, resultsLabel, showMoreLabel, allLabel, noFoundTitleLabel, noFoundLabel } =
    OFFER_FILTER_I18N[locale.language]

  const hasFilterActive = !state.activeTags.includes(SHOW_ALL_PRODUCTS)

  let content: React.ReactNode

  if (state.filteredCards.length === 0)
    content = <FlexGrid.Col>{ErrorCard(Search, noFoundTitleLabel(resultsText), noFoundLabel)}</FlexGrid.Col>
  else {
    const orderedCards = orderCards(state.filteredCards, hasFilterActive, fullWidthCardPosition)
    content = orderedCards
      .slice(0, getCardsToShow(orderedCards, state.colsToShow))
      .map((card) => renderOfferCard(card, hasFilterActive, locale.language, isMobile, equalCardHeight))
  }

  const findTagSelectedFromFilter = (selectedTags: string[], activeTags: string[]) => {
    const allTags = selectedTags.concat(activeTags)
    const clickedTag = allTags.find((searchTag) => allTags.filter((tag) => tag === searchTag).length === 1)
    return filterTags.find(({ id }) => id === clickedTag)?.label || allLabel
  }

  const onFilterChangeHandler = (selectedTags: string[]) => {
    const selectedLabels = selectedTags.map((tagId) => {
      const tag = filterTags.find((t) => t.id === tagId)
      return tag ? tag.urlTag : tagId
    })

    const params = new URLSearchParams(window.location.search)
    params.set('tags', selectedLabels.join(','))
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)

    alloyEvents.filterTag({
      sysID,
      entryTitle,
      tag: findTagSelectedFromFilter(selectedTags, state.activeTags),
      isNewFilter: selectedTags.length > state.activeTags.length,
    })

    dispatch({ type: ActionType.TAG_FILTER_CHANGED, payload: selectedTags })
  }

  const onSubmitSearchHandler = (searchValue: string) => {
    // Store analytics data to be sent after state updates
    pendingSearchAnalytics.current = {
      searchTerm: searchValue,
      isFirstSearch: state.isFirstSearch,
    }

    // Dispatch the search action to update state
    dispatch({ type: ActionType.SEARCH_FILTER_CHANGED, payload: searchValue })
  }

  if (!isClient) {
    return null
  }

  return (
    <FlexGrid dataSet={mapDataAttrsToDataSet(rest)} testID="offers-filter-component">
      {!!heading && (
        <>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Typography block variant={{ size: 'h1' }}>
                {heading}
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>

          <Spacer space={6} />
        </>
      )}
      <FlexGrid.Row>
        <FlexGrid.Col>
          {!enableSearch && <InputLabel label={filterByProductLabel} copy={locale.language} />}
        </FlexGrid.Col>
      </FlexGrid.Row>

      <Spacer space={3} />

      <FlexGrid.Row {...(enableSearch && isMobile && { xsReverse: true, mdReverse: false })}>
        <FlexGrid.Col {...(enableSearch && { md: 8, xl: 9 })}>
          <Tags
            items={[{ id: SHOW_ALL_PRODUCTS, label: allLabel }, ...filterTags]}
            values={state.activeTags}
            onChange={onFilterChangeHandler}
          />
        </FlexGrid.Col>
        {enableSearch && (
          <FlexGrid.Col md={4} xl={3}>
            <SearchBar
              value={state.searchInputValue}
              onChange={(value) => dispatch({ type: ActionType.SEARCH_INPUT_CHANGED, payload: value })}
              onClear={() => {
                pendingSearchAnalytics.current = null
                dispatch({ type: ActionType.CLEAR_SEARCH })
              }}
              copy={locale.language}
              testID="search-component"
              onSubmit={onSubmitSearchHandler}
            />
            <Spacer space={{ xs: 3, md: 0 }} />
          </FlexGrid.Col>
        )}
      </FlexGrid.Row>

      <Spacer space={7} />

      <FlexGrid.Row>
        <FlexGrid.Col>
          <Typography block variant={{ colour: 'secondary' }}>
            {resultsLabel(state.filteredCards.length, state.allCards.length, resultsText)}
          </Typography>
        </FlexGrid.Col>
      </FlexGrid.Row>

      <Spacer space={5} />

      {!!content && <FlexGrid.Row>{content}</FlexGrid.Row>}

      {state.showMoreEnabled && (
        <>
          <Spacer space={3} />

          <FlexGrid.Row>
            <FlexGrid.Col horizontalAlign="center">
              <Button
                icon={CaretDown}
                iconPosition="right"
                tokens={{ textLine: 'none' }}
                variant={{ text: true }}
                onPress={() => dispatch({ type: ActionType.SHOW_MORE })}
              >
                {showMoreLabel + resultsText}
              </Button>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </>
      )}
    </FlexGrid>
  )
}

const validateRepeatedTags = (tags: TagProps[]) =>
  !!tags &&
  tags.length &&
  !Object.values(
    tags.reduce(
      (a, e) => {
        a[e.label] = ++a[e.label] || 0
        return a
      },
      { [OFFER_FILTER_I18N.en.allLabel]: 0, [OFFER_FILTER_I18N.fr.allLabel]: 0 }
    )
  ).some((n) => n > 0)

export default WithEarlyReturn<OffersFilterProps>(
  OffersFilter,
  'filterTags',
  validateRepeatedTags,
  'Tags should have unique label'
)
